// Phase 9 R2 — Manual quantity adjustment (signed delta + required notes).
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useAdjustInventoryQuantity } from '@/hooks/inventory/useAdjustInventoryQuantity';
import type { InventoryItem } from '@/hooks/inventory/useInventoryItems';
import { useEffect } from 'react';

const schema = z.object({
  delta: z.coerce
    .number()
    .refine((n) => n !== 0, { message: 'Adjustment must be non-zero' }),
  notes: z.string().trim().min(1, 'Notes are required for audit'),
});

type FormValues = z.infer<typeof schema>;

export function AdjustQuantityDialog({
  item,
  open,
  onOpenChange,
}: {
  item: InventoryItem | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const adjust = useAdjustInventoryQuantity();
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { delta: 0, notes: '' },
  });

  useEffect(() => {
    if (open) form.reset({ delta: 0, notes: '' });
  }, [open]);

  const watchDelta = form.watch('delta');
  const onHand = item?.quantity_on_hand ?? 0;
  const previewAfter = onHand + Number(watchDelta || 0);

  const submit = async (values: FormValues) => {
    if (!item) return;
    await adjust.mutateAsync({ id: item.id, delta: values.delta, notes: values.notes });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Adjust Quantity</DialogTitle>
          <DialogDescription>
            Use signed delta for corrections (count discrepancies, shrinkage, true-ups).
            Restocks should use the Restock action instead.
          </DialogDescription>
        </DialogHeader>

        {item && (
          <div className="rounded-md border p-3 text-sm">
            <div className="font-medium">{item.name}</div>
            <div className="text-muted-foreground">{item.part_number}</div>
            <div className="mt-2">
              On Hand: <span className="font-mono">{onHand}</span>
              {' → '}
              <span className="font-mono">{previewAfter}</span>
            </div>
          </div>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(submit)} className="space-y-4">
            <FormField name="delta" control={form.control} render={({ field }) => (
              <FormItem>
                <FormLabel>Adjustment (signed)</FormLabel>
                <FormControl>
                  <Input type="number" step="1" placeholder="e.g. -3 or 5" {...field} />
                </FormControl>
                <FormDescription>
                  Negative numbers reduce on-hand; positive increase it.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )} />
            <FormField name="notes" control={form.control} render={({ field }) => (
              <FormItem>
                <FormLabel>Notes (required)</FormLabel>
                <FormControl>
                  <Textarea
                    rows={3}
                    placeholder="Reason for adjustment (e.g. cycle count discrepancy, damaged unit)"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )} />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={adjust.isPending}>
                {adjust.isPending ? 'Submitting…' : 'Apply Adjustment'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
